import { useEffect, useState } from "react";
import { PageHeader } from "../App";
import { 
  Export, 
  FileJs, 
  FileText,
  CheckSquare,
  Square,
  Download,
  Spinner,
  Info
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import api from "../lib/api";

const FormatCard = ({ format, selected, onClick, icon: Icon, name, description, example }) => {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 border rounded-md transition-all duration-200 ${
        selected 
          ? 'border-blue-500 bg-blue-500/10' 
          : 'border-zinc-800 bg-zinc-900/30 hover:border-zinc-700'
      }`}
      data-testid={`format-${format}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded flex items-center justify-center ${
          selected ? 'bg-blue-500/20' : 'bg-zinc-800'
        }`}>
          <Icon weight="duotone" className={`w-5 h-5 ${selected ? 'text-blue-400' : 'text-zinc-400'}`} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className={`font-medium ${selected ? 'text-blue-300' : 'text-zinc-200'}`}>{name}</p>
            {selected && <CheckSquare weight="fill" className="w-4 h-4 text-blue-400" />}
          </div>
          <p className="text-xs text-zinc-500 mt-1">{description}</p>
        </div>
      </div>
      <div className="mt-3 p-2 bg-zinc-950 rounded text-xs font-mono text-zinc-400 overflow-x-auto">
        <pre>{example}</pre>
      </div>
    </button>
  );
};

const DatasetCheckbox = ({ dataset, selected, onToggle }) => {
  return (
    <button
      onClick={onToggle}
      className={`flex items-center gap-3 p-3 border rounded-md transition-colors ${
        selected ? 'border-blue-500/50 bg-blue-500/5' : 'border-zinc-800 hover:border-zinc-700'
      }`}
      data-testid={`dataset-${dataset.id}`}
    >
      {selected ? (
        <CheckSquare weight="fill" className="w-5 h-5 text-blue-400" />
      ) : (
        <Square weight="regular" className="w-5 h-5 text-zinc-500" />
      )}
      <div className="flex-1 text-left">
        <p className="text-sm text-zinc-200">{dataset.name}</p>
        <p className="text-xs text-zinc-500">{dataset.conversation_count} conversations</p>
      </div>
      <span className={`badge badge-${
        dataset.source_platform === 'chatgpt' ? 'green' :
        dataset.source_platform === 'perplexity' ? 'blue' : 'yellow'
      }`}>
        {dataset.source_platform}
      </span>
    </button>
  );
};

const exportFormats = [
  {
    format: 'jsonl',
    name: 'JSONL (Standard)',
    description: 'Standard format for fine-tuning. One JSON object per line.',
    icon: FileJs,
    example: '{"messages":[{"role":"user","content":"Hi"},{"role":"assistant","content":"Hello!"}]}'
  },
  {
    format: 'alpaca',
    name: 'Alpaca Format',
    description: 'Instruction/input/output format for instruction tuning.',
    icon: FileText,
    example: '{"instruction":"What is AI?","input":"","output":"AI is..."}'
  },
  {
    format: 'chatml',
    name: 'ChatML Format',
    description: 'OpenAI ChatML format with special tokens.',
    icon: FileText,
    example: '<|im_start|>user\\nHello<|im_end|>\\n<|im_start|>assistant\\nHi!<|im_end|>'
  }
];

export default function ExportPage() {
  const [datasets, setDatasets] = useState([]);
  const [selectedDatasets, setSelectedDatasets] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState('jsonl');
  const [includeSystem, setIncludeSystem] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDatasets();
  }, []);

  const loadDatasets = async () => {
    try {
      const data = await api.getDatasets();
      setDatasets(data);
    } catch (error) {
      console.error("Failed to load datasets:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDataset = (id) => {
    setSelectedDatasets(prev => 
      prev.includes(id) 
        ? prev.filter(d => d !== id)
        : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedDatasets(datasets.map(d => d.id));
  };

  const deselectAll = () => {
    setSelectedDatasets([]);
  };

  const handleExport = async () => {
    if (selectedDatasets.length === 0) {
      toast.error("Please select at least one dataset");
      return;
    }

    setExporting(true);
    try {
      const blob = await api.exportDatasets(selectedDatasets, selectedFormat, includeSystem);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `training_data_${selectedFormat}.${selectedFormat === 'chatml' ? 'txt' : 'jsonl'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Export completed successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export datasets");
    } finally {
      setExporting(false);
    }
  };

  const totalConversations = datasets
    .filter(d => selectedDatasets.includes(d.id))
    .reduce((sum, d) => sum + d.conversation_count, 0);

  return (
    <div>
      <PageHeader 
        title="Export" 
        subtitle="Generate training-ready datasets"
      />
      
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Dataset Selection */}
          <div className="chart-container">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">
                Select Datasets
              </h3>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={selectAll} data-testid="select-all-btn">
                  Select All
                </Button>
                <Button variant="ghost" size="sm" onClick={deselectAll} data-testid="deselect-all-btn">
                  Clear
                </Button>
              </div>
            </div>
            
            {loading ? (
              <div className="space-y-2">
                {[1,2,3].map(i => (
                  <div key={i} className="h-16 bg-zinc-800 rounded animate-pulse"></div>
                ))}
              </div>
            ) : datasets.length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto scrollbar-thin">
                {datasets.map(dataset => (
                  <DatasetCheckbox
                    key={dataset.id}
                    dataset={dataset}
                    selected={selectedDatasets.includes(dataset.id)}
                    onToggle={() => toggleDataset(dataset.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <Export weight="thin" className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">No datasets available</p>
                <p className="text-zinc-600 text-xs mt-1">Upload some files first</p>
              </div>
            )}
            
            {selectedDatasets.length > 0 && (
              <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-md">
                <p className="text-sm text-blue-300">
                  {selectedDatasets.length} dataset{selectedDatasets.length > 1 ? 's' : ''} selected
                  <span className="text-blue-400 ml-2">
                    ({totalConversations.toLocaleString()} conversations)
                  </span>
                </p>
              </div>
            )}
          </div>
          
          {/* Format Selection */}
          <div className="chart-container">
            <h3 className="text-sm font-medium text-zinc-400 mb-4 uppercase tracking-wider">
              Export Format
            </h3>
            
            <div className="space-y-3">
              {exportFormats.map(format => (
                <FormatCard
                  key={format.format}
                  {...format}
                  selected={selectedFormat === format.format}
                  onClick={() => setSelectedFormat(format.format)}
                />
              ))}
            </div>
            
            {/* Options */}
            <div className="mt-6 pt-6 border-t border-zinc-800">
              <h4 className="text-sm font-medium text-zinc-400 mb-4">Options</h4>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label htmlFor="include-system" className="text-sm text-zinc-300">
                    Include system messages
                  </Label>
                  <Info weight="regular" className="w-4 h-4 text-zinc-500" />
                </div>
                <Switch
                  id="include-system"
                  checked={includeSystem}
                  onCheckedChange={setIncludeSystem}
                  data-testid="include-system-toggle"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Export Button */}
        <div className="flex justify-end">
          <Button
            size="lg"
            onClick={handleExport}
            disabled={exporting || selectedDatasets.length === 0}
            data-testid="export-btn"
            className="min-w-[200px]"
          >
            {exporting ? (
              <>
                <Spinner weight="bold" className="w-5 h-5 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download weight="bold" className="w-5 h-5 mr-2" />
                Export Dataset
              </>
            )}
          </Button>
        </div>

        {/* Training Tips */}
        <div className="chart-container">
          <h3 className="text-sm font-medium text-zinc-400 mb-4 uppercase tracking-wider">
            Training Tips
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-zinc-900/30 rounded-md">
              <p className="text-sm text-zinc-200 mb-2">For Ollama / llama.cpp</p>
              <p className="text-xs text-zinc-500">
                Use JSONL format. Convert to GGUF using llama.cpp tools. 
                Run with: <code className="text-cyan-400">ollama create mymodel -f Modelfile</code>
              </p>
            </div>
            <div className="p-4 bg-zinc-900/30 rounded-md">
              <p className="text-sm text-zinc-200 mb-2">For OpenAI Fine-tuning</p>
              <p className="text-xs text-zinc-500">
                Use JSONL format with messages array. Upload via API or dashboard.
                Minimum 10 examples recommended.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
